import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchInspector } from '../lib/api';
import type { InspectorDetail, InstagramPost, FeedReview } from '../lib/types';
import { PageHero } from '../components/SiteChrome';
import { contactFor } from '../lib/inspectorContacts';
import ContactModal from '../components/ContactModal';

export default function InspectorProfilePage() {
  const { slug = '' } = useParams();
  const [data, setData] = useState<InspectorDetail | null>(null);
  const [igPosts, setIgPosts] = useState<InstagramPost[]>([]);
  const [reviews, setReviews] = useState<FeedReview[]>([]);
  const [igShown, setIgShown] = useState(9);
  const [revShown, setRevShown] = useState(9);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    setIgPosts([]);
    setReviews([]);
    setIgShown(9);
    setRevShown(9);
    fetchInspector(slug).then(setData).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
    // Static Instagram feed (built by the IG poller). Routed to this inspector
    // by the name in each post's caption. Best-effort — never blocks the page.
    fetch('/ig-feed.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((f) => setIgPosts(f?.byInspector?.[slug] ?? []))
      .catch(() => { /* feed not present yet — section stays in placeholder */ });
    // Static Google reviews feed — reviews that mention this inspector by name.
    // Sourced once from the existing review history; swaps to the live GBP API
    // once that access is approved. Best-effort — never blocks the page.
    fetch('/reviews-feed.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((f) => setReviews(f?.byInspector?.[slug] ?? []))
      .catch(() => { /* feed not present yet */ });
  }, [slug]);

  if (error) {
    return (
      <section className="max-w-3xl mx-auto px-5 py-20">
        <h1 className="text-2xl font-semibold text-navy">Inspector not found</h1>
        <p className="mt-3 text-slate-600">
          We couldn't find that profile. <Link to="/inspection-team" className="text-accent underline">Back to the team</Link>.
        </p>
      </section>
    );
  }
  if (!data) {
    return <p className="max-w-3xl mx-auto px-5 py-20 text-slate-500">Loading…</p>;
  }

  const name = data.name ?? `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();
  const roleLine = [data.title, data.companyName].filter(Boolean).join(' · ');
  const posts = igPosts.length ? igPosts : (data.instagramPosts ?? []);
  const c = contactFor(data);
  const firstName = data.firstName ?? name;

  const heroAside = (
    <div className="bg-white/10 backdrop-blur rounded-xl border border-white/15 px-4 py-3 flex items-center gap-3 w-fit lg:ml-auto">
      <div className="text-center pr-3 border-r border-white/15">
        <div className="text-amber-400 text-xs leading-none">★★★★★</div>
        <p className="text-base font-extrabold leading-tight mt-0.5">3,800+</p>
        <p className="text-[10px] text-slate-300 leading-none">5-star reviews</p>
      </div>
      <a href={c.bookingUrl} target="_blank" rel="noreferrer"
         className="bg-accent hover:bg-accent-dark text-white font-semibold text-sm px-3 py-2 rounded-lg whitespace-nowrap transition-colors">
        Book {firstName}
      </a>
      <button onClick={() => setContactOpen(true)}
         className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold text-sm px-3 py-2 rounded-lg whitespace-nowrap transition-colors">
        Contact {firstName}
      </button>
    </div>
  );

  return (
    <>
      {contactOpen ? <ContactModal inspectorName={name} onClose={() => setContactOpen(false)} /> : null}
      <PageHero
        title={name}
        subtitle={roleLine || undefined}
        aside={heroAside}
        breadcrumb={<><Link to="/inspection-team" className="hover:text-white">Our Team</Link> <span className="mx-1">/</span> <span className="text-white">{name}</span></>}
      />

      <main className="max-w-7xl mx-auto px-5 py-12 grid lg:grid-cols-12 gap-10 items-start">
        {/* Sticky left info card */}
        <aside className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {data.avatarUrl ? (
              <img src={data.avatarUrl} alt={name} className="w-full aspect-[4/5] object-cover object-top bg-slate-100" />
            ) : (
              <div className="w-full aspect-[4/5] flex items-center justify-center text-white text-6xl font-extrabold"
                   style={{ background: `linear-gradient(135deg, ${data.color ?? '#475569'}, #0f172a)` }}>
                {data.initials ?? name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
              </div>
            )}
            <div className="p-6 space-y-4">
              {data.title ? <InfoRow label="Role" value={data.title} /> : null}
              {data.cpbcLicence ? <InfoRow label="CPBC Licence" value={`#${data.cpbcLicence}`} /> : null}
              {data.languages && data.languages.length ? (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Languages</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {data.languages.map((l) => (
                      <span key={l} className="text-xs bg-sky-tint rounded-full px-2.5 py-0.5">{l}</span>
                    ))}
                  </div>
                </div>
              ) : null}
              {data.companyName ? (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Company</p>
                  <p className="text-accent font-semibold">{data.companyName}</p>
                </div>
              ) : null}
              <a href="https://inspect.ca/contact/" className="block text-center bg-accent hover:bg-accent-dark text-white font-semibold py-3 rounded-xl mt-2">
                Book with {data.firstName ?? name}
              </a>
            </div>
          </div>
        </aside>

        {/* Main column */}
        <div className="lg:col-span-8 space-y-12">
          {data.bio ? (
            <section>
              <h2 className="text-2xl font-extrabold text-navy">About {data.firstName ?? name}</h2>
              <p className="mt-4 text-slate-700 leading-relaxed text-[15px] whitespace-pre-line">{data.bio}</p>
            </section>
          ) : null}

          {/* Instagram — populated via the Graph API once the token is connected. */}
          <section>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-2xl font-extrabold text-navy">On the job</h2>
              <a href="https://www.instagram.com/inspectcanada/" target="_blank" rel="noreferrer" className="text-sm text-accent font-semibold">@inspectcanada →</a>
            </div>
            {posts.length ? (
              <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                {posts.slice(0, igShown).map((p) => (
                  <a key={p.id} href={p.permalink ?? '#'} target="_blank" rel="noreferrer"
                     className="aspect-square rounded-xl overflow-hidden bg-slate-100 block group relative">
                    <img src={p.imageUrl} alt={p.caption?.slice(0, 80) ?? 'Inspection post'}
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </a>
                ))}
              </div>
              {posts.length > igShown ? (
                <div className="text-center mt-5">
                  <button onClick={() => setIgShown((n) => n + 9)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-navy font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Load more photos
                  </button>
                </div>
              ) : null}
              </>
            ) : (
              <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center">
                <p className="text-slate-500 text-sm">
                  Recent inspection posts featuring {data.firstName ?? name} will appear here.
                </p>
                <a href="https://www.instagram.com/inspectcanada/" target="_blank" rel="noreferrer"
                   className="inline-block mt-3 text-accent font-semibold text-sm">Follow @inspectcanada on Instagram →</a>
              </div>
            )}
          </section>

          {/* Google reviews that mention this inspector by name. */}
          {reviews.length ? (
            <section>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-2xl font-extrabold text-navy">What clients say about {data.firstName ?? name}</h2>
                <span className="text-sm text-slate-500">
                  {reviews.length} recent Google review{reviews.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                {reviews.slice(0, revShown).map((r, i) => <ReviewCard key={i} review={r} />)}
              </div>
              {reviews.length > revShown ? (
                <div className="text-center mt-5">
                  <button onClick={() => setRevShown((n) => n + 9)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-navy font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Load more reviews
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          <div>
            <Link to="/inspection-team" className="text-accent font-semibold text-sm">← Back to the team</Link>
          </div>
        </div>
      </main>
    </>
  );
}

function ReviewCard({ review }: { review: FeedReview }) {
  const stars = Math.max(0, Math.min(5, review.rating || 5));
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col">
      <div className="flex items-center gap-2">
        {review.photo ? (
          <img src={review.photo} alt={review.author} loading="lazy"
               className="w-8 h-8 rounded-full object-cover bg-slate-100" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center font-bold text-xs">
            {(review.author || '?').trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-navy text-xs truncate">{review.author || 'Google user'}</p>
          {review.date ? <p className="text-[10px] text-slate-400">{review.date}</p> : null}
        </div>
        <span className="ml-auto text-amber-400 text-xs tracking-tight" aria-label={`${stars} out of 5 stars`}>
          {'★'.repeat(stars)}
        </span>
      </div>
      <p className="mt-2 text-slate-700 text-[13px] leading-relaxed line-clamp-[8]">{review.text}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-ink font-medium">{value}</p>
    </div>
  );
}
